import { type Control, type Noop, useController } from 'react-hook-form';
import type { ExtractKeys } from '../types/extractKeysOfType';

export namespace useField {
    export type Props<
        T extends NonNullable<unknown> | null,
        O extends object,
    > = {
        name: ExtractKeys<T, O>;
        control: Control<O>;
        onChange?: (newValue: NoInfer<T>) => void;
    };

    export type Result<T extends NonNullable<unknown> | null> = {
        name: string;
        error: string | undefined;
        value: T;
        onChange: (newValue: T) => void;
        onBlur: Noop;
    };
}

export const useField = <
    T extends NonNullable<unknown> | null,
    O extends object,
>({
    name,
    control,
    onChange,
}: useField.Props<T, O>): useField.Result<T> => {
    const {
        fieldState: { error },
        field: { value, onChange: fieldOnChange, onBlur },
    } = useController<O>({ name, control });

    const handleChange = (newValue: T) => {
        fieldOnChange(newValue);
        onChange?.(newValue);
    };

    return {
        name,
        error: error?.message,
        value: value as T,
        onChange: handleChange,
        onBlur,
    };
};
